import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './TermsAndConditionsPage.css';

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { passengerData, flight, additionalOptions, acceptedTerms } = location.state || {};

  const handleBackToBooking = () => {
    // Navigate back to passenger info page with preserved form data
    navigate('/passenger-info', {
      state: {
        flight: flight,
        passengerData: passengerData,
        additionalOptions: additionalOptions,
        acceptedTerms: acceptedTerms
      }
    });
  };

  return (
    <div className="terms-page">
      <Header />
      
      <div className="terms-container">
        <div className="terms-content">
          <h1 className="terms-title">Terms and Conditions</h1>
          <p className="terms-last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <section className="terms-section">
            <h2 className="terms-section-title">1. Acceptance of Terms</h2>
            <p className="terms-text">
              By accessing and using this flight booking platform, you accept and agree to be bound by the terms and conditions set forth below. 
              If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">2. Booking and Payment</h2>
            <p className="terms-text">
              All flight bookings are subject to availability and confirmation by the airline. Prices displayed are subject to change until payment is confirmed. 
              We accept various payment methods including credit cards, debit cards, and bank transfers. All transactions are processed securely.
            </p>
            <p className="terms-text">
              Once a booking is confirmed and payment is processed, you will receive a confirmation email with your booking reference. 
              Please keep this reference safe as it will be required for any changes or cancellations.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">3. Cancellation and Refund Policy</h2>
            <p className="terms-text">
              Cancellation policies vary by airline and fare type. Some tickets may be non-refundable or subject to cancellation fees. 
              Please review the specific terms of your selected fare before completing your booking.
            </p>
            <p className="terms-text">
              Refunds, when applicable, will be processed to the original payment method within 7-14 business days. 
              Processing fees may apply as per airline policies.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">4. Changes and Modifications</h2>
            <p className="terms-text">
              Changes to bookings may be permitted subject to airline policies and fare rules. Change fees and fare differences may apply. 
              Please contact our customer service team for assistance with modifications.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">5. Passenger Information</h2>
            <p className="terms-text">
              You are responsible for providing accurate passenger information including names, dates of birth, passport numbers, and contact details. 
              Incorrect information may result in additional fees or denied boarding. We are not liable for any issues arising from incorrect information provided.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">6. Travel Documents and Requirements</h2>
            <p className="terms-text">
              It is your responsibility to ensure you have valid travel documents including passports, visas, and any required health certificates. 
              We recommend checking entry requirements for your destination well in advance of travel.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">7. Baggage Allowance</h2>
            <p className="terms-text">
              Baggage allowances vary by airline, route, and fare type. Please check your specific booking for included baggage allowances. 
              Additional baggage can be purchased subject to availability and airline policies.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">8. Flight Delays and Cancellations</h2>
            <p className="terms-text">
              We are not responsible for flight delays, cancellations, or schedule changes made by airlines. 
              In such cases, please contact the airline directly or our customer service team for assistance.
            </p>
            <p className="terms-text">
              Compensation for delays or cancellations is subject to airline policies and applicable regulations.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">9. Liability</h2>
            <p className="terms-text">
              We act as an intermediary between you and the airline. Our liability is limited to the booking services we provide. 
              We are not liable for any loss, damage, or inconvenience caused by airline actions, weather conditions, or other circumstances beyond our control.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">10. Privacy and Data Protection</h2>
            <p className="terms-text">
              We are committed to protecting your personal information. Your data will be used solely for booking purposes and in accordance with our Privacy Policy. 
              We may share necessary information with airlines and service providers to complete your booking.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">11. Prohibited Activities</h2>
            <p className="terms-text">
              You agree not to use our platform for any unlawful purpose or in any way that could damage, disable, or impair the service. 
              Fraudulent bookings or misuse of the platform may result in legal action.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">12. Intellectual Property</h2>
            <p className="terms-text">
              All content on this platform, including text, graphics, logos, and software, is the property of our company and is protected by copyright and other intellectual property laws.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">13. Modifications to Terms</h2>
            <p className="terms-text">
              We reserve the right to modify these terms and conditions at any time. Changes will be effective immediately upon posting. 
              Continued use of our services after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">14. Contact Information</h2>
            <p className="terms-text">
              For questions, concerns, or support regarding these terms and conditions, please contact our customer service team through the contact page or email us at support@flightbooking.com.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">15. Governing Law</h2>
            <p className="terms-text">
              These terms and conditions are governed by the laws of Nigeria. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of Nigerian courts.
            </p>
          </section>

          <div className="terms-actions">
            <button onClick={handleBackToBooking} className="terms-back-button">
              ← Back to Booking
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsAndConditionsPage;
